import type { Message } from '../lib/api';
import type { SenderIdentity } from '../lib/spaceIdentity';
import { useRelativeTime } from '../hooks/useRelativeTime';

interface MessageMetaProps {
  message: Message;
  /** The resolved sender (display name + member glyph). Absent on surfaces with
   *  no roster (DMs), where the byline falls back to the raw handle. */
  sender?: SenderIdentity;
}

export function MessageMeta({ message, sender }: MessageMetaProps) {
  const sentAt = new Date(message.sent_at);
  // Live-ticking relative string. Only USED for the recent (<1h) bucket below,
  // but the hook must run unconditionally (rules of hooks). For that bucket
  // `formatRelativeTime` emits the same "just now" / "Nm ago" strings the old
  // one-shot formatter produced — now self-updating without an unrelated
  // re-render (#1251). Older buckets keep their absolute formatting unchanged.
  const liveRelative = useRelativeTime(message.sent_at);
  const display = formatMessageTime(sentAt, liveRelative);
  const rawHandle = message.sender_persona.trim() || 'System';

  return (
    <header className="message-meta">
      {sender ? (
        // Display name + member glyph; the exact handle waits on hover/focus so
        // it is never lost, only demoted (T-0200).
        <span className="message-meta-persona" title={sender.handle}>
          <span className="message-meta-glyph" aria-hidden="true">
            {sender.glyph}
          </span>
          {sender.name}
        </span>
      ) : (
        <span className="message-meta-persona" title={rawHandle}>
          {rawHandle}
        </span>
      )}
      {message.addressed_to && (
        <span className="message-meta-addressed-to">
          → @{message.addressed_to}
        </span>
      )}
      <a
        className="message-meta-time"
        href={`#${message.message_id}`}
        title={message.sent_at}
      >
        {display}
      </a>
    </header>
  );
}

function formatMessageTime(sentAt: Date, liveRelative: string): string {
  if (Number.isNaN(sentAt.getTime())) return 'Unknown time';

  const elapsedMs = Date.now() - sentAt.getTime();

  // Recent (<1h): live-ticking relative ("just now" / "Nm ago"). Identical
  // strings to the prior one-shot code, now self-updating (#1251).
  if (elapsedMs >= 0 && elapsedMs < 60 * 60_000) {
    return liveRelative;
  }

  // 1–24h: absolute time-of-day (unchanged).
  if (elapsedMs >= 0 && elapsedMs < 24 * 60 * 60_000) {
    return sentAt.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  // Older than 24h (or a future/clock-skew stamp): absolute date (unchanged).
  return sentAt.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
